import { LegalPageLayout } from '../../components/layout/LegalPageLayout';
import { Helmet } from 'react-helmet-async';

export const TermsPage: React.FC = () => (
  <LegalPageLayout title="Terms of Service" useH1={true}>
    <Helmet prioritizeSeoTags>
      <title>Terms of Service | Cognitive Bias Lab</title>
      <meta name="description" content="Read the terms and conditions for using Cognitive Bias Lab. Learn about our policies, user responsibilities, and terms governing the use of our platform." />
      <meta name="robots" content="noindex" />
      <link rel="canonical" href="https://www.cognitivebiaslab.com/terms/" key="canonical" data-source="page-component" />
      
      {/* OpenGraph tags for better social sharing */}
      <meta property="og:title" content="Terms of Service | Cognitive Bias Lab" />
      <meta property="og:description" content="Read the terms and conditions for using Cognitive Bias Lab. Learn about our policies, user responsibilities, and terms governing the use of our platform." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.cognitivebiaslab.com/terms/" />
      <meta property="og:site_name" content="Cognitive Bias Lab" />
      <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:type" content="image/webp" />
    </Helmet>
    <div className="space-y-8 max-w-4xl mx-auto">
      <div className="text-sm text-gray-500 italic">
        Last Updated: 31 March 2025
      </div>

      <p className="text-gray-600 leading-relaxed">
        Welcome to Cognitive Bias Lab. Please read these Terms of Service ("Terms") carefully before using our website, 
        which is provided free of charge as an educational resource designed to teach about cognitive biases in an 
        interactive manner. By accessing or using our website, you agree to be bound by these Terms.
      </p>

    <div className="space-y-8">
      <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">1. Acceptance of Terms</h2>
          <p className="text-gray-600 leading-relaxed">
            By using Cognitive Bias Lab, you confirm that you have read, understood, and agree to these Terms. 
            If you do not agree to these Terms, please do not use the website.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">2. Changes to Terms</h2>
          <p className="text-gray-600 leading-relaxed">
            We reserve the right to update or modify these Terms at any time. Any changes will be posted on this 
            page with an updated effective date. Continued use of the website after changes are made constitutes 
            your acceptance of the new Terms.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">3. Use of the Website</h2>
          <p className="text-gray-600 leading-relaxed">
            Cognitive Bias Lab is intended solely for personal, non-commercial use. The website is provided "as is" 
            without any warranties regarding its reliability or accuracy. While we strive for correctness, the content 
            may contain errors or omissions. Use the information at your own risk.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">4. Educational Content Disclaimer</h2>
          <p className="text-gray-600 leading-relaxed">
            All content on Cognitive Bias Lab is provided for educational purposes only. The information is not 
            intended as professional advice—legal, financial, or otherwise—and should not be relied upon as such. 
            If you require professional guidance, please consult a qualified expert.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">5. Intellectual Property</h2>
          <p className="text-gray-600 leading-relaxed">
            All content, graphics, text, and other materials on Cognitive Bias Lab are the intellectual property 
            of Cognitive Bias Lab or its content suppliers and are protected by copyright and other intellectual 
            property laws. You may not reproduce, distribute, or create derivative works without our express written 
            permission. Any trademarks, service marks, or logos used on the website are the property of their 
            respective owners.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">6. Privacy and Cookies</h2>
          <p className="text-gray-600 leading-relaxed">
            Your privacy is important to us. We implement a consent management system that gives you full control 
            over what data is collected during your visit.
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li>We only use analytics tools with your explicit consent</li>
            <li>Essential cookies required for site functionality may be placed without consent</li>
            <li>You can manage your preferences at any time through the "Manage Cookies" link in our footer</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            For more detailed information about how we handle your data, please refer to our <a href="/privacy/" className="text-indigo-700 hover:text-indigo-500">Privacy Policy</a>.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">7. External Links</h2>
          <p className="text-gray-600 leading-relaxed">
            Our website may contain links to external websites. These links are provided for your convenience only. 
            Cognitive Bias Lab is not responsible for the content or practices of any third-party websites and does 
            not endorse or guarantee their accuracy or reliability. You access these external sites at your own risk.
        </p>
      </section>

      <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">8. Limitation of Liability</h2>
          <p className="text-gray-600 leading-relaxed">
            To the fullest extent permitted by applicable law, Cognitive Bias Lab and its operators shall not be 
            liable for any direct, indirect, incidental, or consequential damages arising from the use or inability 
            to use our website or its content. This includes any errors or omissions in the content provided.
        </p>
      </section>

      <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">9. Your Rights and Responsibilities</h2>
          <p className="text-gray-600 leading-relaxed">
            When using our website, you have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li>Control your privacy through our consent management system</li>
            <li>Access our educational content free of charge</li>
            <li>Contact us with questions or concerns</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            You are responsible for:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li>Using the website in a lawful manner</li>
            <li>Making informed decisions about consent and privacy preferences</li>
            <li>Understanding that the educational content is not professional advice</li>
          </ul>
      </section>

      <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">10. Governing Law</h2>
          <p className="text-gray-600 leading-relaxed">
            These Terms shall be governed by and construed in accordance with Irish law. Any disputes arising in 
            connection with these Terms or the use of the website shall be subject to the exclusive jurisdiction 
            of the courts in Ireland.
        </p>
      </section>

      <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">11. Contact Information</h2>
          <p className="text-gray-600 leading-relaxed">
            If you have any questions about these Terms or the website, please contact us at <a href="mailto:contact@cognitivebiaslab.com" className="text-indigo-700 hover:text-indigo-500">contact@cognitivebiaslab.com</a>.
        </p>
      </section>
      </div>
    </div>
  </LegalPageLayout>
); 